import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const STORAGE_KEY = "pp_seller_slug";

const SupportContext = createContext(null);

export function SupportProvider({ children }) {
  const [sellerSlug, setSellerSlugState] = useState(null);
  const [support, setSupport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Läs initial sellerSlug från localStorage eller URL parameter
  useEffect(() => {
    try {
      // Check URL parameters first (from share links)
      const urlParams = new URLSearchParams(window.location.search);
      const urlSellerSlug = urlParams.get("s");
      
      if (urlSellerSlug) {
        // Auto-assign seller from share link
        setSellerSlugState(urlSellerSlug);
        localStorage.setItem(STORAGE_KEY, urlSellerSlug);
        return;
      }
      
      // Fallback to localStorage
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setSellerSlugState(stored);
      }
    } catch {
      // Ignorera problem med localStorage
    }
  }, []);

  // Hämta support-info när sellerSlug ändras
  useEffect(() => {
    if (!sellerSlug) {
      setSupport(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setSupport((prev) => {
      const prevSlug = prev?.sellerSlug || prev?.seller_slug;
      return prevSlug === sellerSlug ? prev : null;
    });

    api
      .checkoutSupport(sellerSlug)
      .then((s) => {
        if (cancelled) return;
        setSupport(s);
        // Synka ev. normaliserad slug till localStorage
        try {
          if (s?.sellerSlug) {
            localStorage.setItem(STORAGE_KEY, s.sellerSlug);
            setSellerSlugState(s.sellerSlug);
          } else {
            localStorage.setItem(STORAGE_KEY, sellerSlug);
          }
        } catch {
          // non-blocking
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSupport(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sellerSlug]);

  const setSellerSlug = useCallback((slug) => {
    const value = slug || null;
    setSellerSlugState(value);
    setSupport((prev) => {
      if (!value) return null;
      const prevSlug = prev?.sellerSlug || prev?.seller_slug;
      return prevSlug === value ? prev : null;
    });
    try {
      if (value) {
        localStorage.setItem(STORAGE_KEY, value);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  const clearSupport = useCallback(() => {
    setSellerSlug(null);
    setSupport(null);
  }, [setSellerSlug]);

  const value = {
    sellerSlug,
    support,
    loading,
    setSellerSlug,
    clearSupport,
  };

  return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
}

export function useSupport() {
  const ctx = useContext(SupportContext);
  if (!ctx) {
    throw new Error("useSupport måste användas inom SupportProvider");
  }
  return ctx;
}


